import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDownToLine, Crown, MessageCircle, MapPin, ShieldCheck, Loader2,
  Heart, LogIn, UserPlus, Smartphone, Check, Star, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { Monogram } from "../components/Monogram";
import { categoryLabel } from "../lib/geo";
import { useI18n } from "../lib/i18n";

/**
 * /join landing — public, instant, no-auth-required marketing page.
 *
 * Reached when someone scans a Habano QR code with their phone camera (or
 * follows a shared link). The page renders the business / vendor / user
 * profile preview and, depending on auth state, lets the visitor:
 *
 *   • Open / install the PWA
 *   • Continue browsing as customer
 *   • Sign in / create an account
 *   • If logged in: check in, save, send friend request, etc.
 *
 * SEO meta tags for WhatsApp / iMessage previews are served separately by the
 * backend `/api/og/{type}/{id}` route — share-sheet copy-link uses that URL.
 */
export default function Join() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const type = params.get("type"); // business | vendor | user
  const id = params.get("id");

  const [meta, setMeta] = useState(null);
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  useEffect(() => {
    if (!type || !id) { setError("Invalid link"); return; }
    api.get(`/join/meta?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`)
      .then(({ data }) => setMeta(data))
      .catch((e) => setError(e.response?.data?.detail || "Profile not found"));
    getMe().then(setMe);
  }, [type, id]);

  // Detect already-installed PWA and bounce straight into the app
  useEffect(() => {
    if (!meta) return;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone && me && meta.deep_link) {
      navigate(meta.deep_link, { replace: true });
    }
  }, [meta, me, navigate]);

  // ---------- Logged-in actions ----------
  const checkIn = async () => {
    if (!meta || meta.type !== "business") return;
    setBusy(true);
    try {
      await api.post("/check-ins", { business_id: meta.id });
      toast.success(`Checked in at ${meta.name}`);
      navigate(meta.deep_link);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const savePlace = async () => {
    if (!meta) return;
    setBusy(true);
    try {
      const target_type = meta.type === "business" ? "business" : "vendor";
      await api.post(`/favorites/${target_type}/${meta.id}`);
      toast.success(meta.type === "business" ? "Place saved" : "Vendor saved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const addFriend = async () => {
    if (!meta || meta.type !== "user") return;
    setBusy(true);
    try {
      await api.post("/qr/scan", { raw_data: `habano:u:${meta.id}` });
      toast.success(`Friend request sent to ${meta.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  // ---------- Desktop redirect screen ----------
  if (!isMobile && meta) {
    return <DesktopShareView meta={meta} />;
  }

  if (error) {
    return (
      <main className="min-h-screen grid place-items-center bg-background px-6" data-testid="join-error">
        <div className="lux-card p-8 text-center max-w-sm">
          <div className="eyebrow text-destructive">Not found</div>
          <div className="font-display text-3xl mt-2 text-foreground">{error}</div>
          <Link to="/" className="btn-gold mt-6 inline-flex">Go to Habano.app</Link>
        </div>
      </main>
    );
  }

  if (!meta) {
    return (
      <main className="min-h-screen grid place-items-center bg-background" data-testid="join-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gold" strokeWidth={1.5} />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="join-page">
      <header className="px-6 pt-7 pb-3 flex items-center gap-2.5">
        <Monogram size={28} />
        <span
          className="font-display tracking-[0.32em] uppercase text-base"
          style={{
            background: "linear-gradient(135deg, #D6B16B 0%, #C8A063 50%, #B8924F 100%)",
            WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
          }}
        >
          HABANO.APP
        </span>
      </header>

      <ProfileCard meta={meta} />

      <section className="px-6 mt-6 space-y-3" data-testid="join-actions">
        {me ? (
          <LoggedInActions meta={meta} busy={busy} onCheckIn={checkIn} onSave={savePlace} onAddFriend={addFriend} />
        ) : (
          <GuestActions meta={meta} />
        )}
      </section>

      <section className="px-6 mt-10 pb-12 text-center">
        <p className="text-sm text-foreground/55">
          {t("join_discover_tagline")}
        </p>
        <button
          data-install-trigger
          data-testid="join-add-to-home"
          className="btn-outline w-full mt-3 justify-center"
          style={{ minHeight: 52 }}
        >
          <ArrowDownToLine className="w-4 h-4" strokeWidth={1.5} /> {t("join_add_to_home")}
        </button>
        {!me && (
          <p className="text-[12px] text-foreground/45 mt-6 leading-relaxed">
            {t("join_create_footer")}
          </p>
        )}
      </section>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Profile card                                                       */
/* ------------------------------------------------------------------ */
function ProfileCard({ meta }) {
  const photo = meta.photo_url;
  const subtitle = meta.type === "business"
    ? [categoryLabel(meta.category), meta.neighborhood].filter(Boolean).join(" · ")
    : meta.type === "vendor"
      ? "Verified Habano vendor"
      : "Habano friend";
  return (
    <section className="px-6 mt-2" data-testid="join-profile-card">
      <div className="lux-card overflow-hidden">
        <div className="relative">
          {photo ? (
            <img
              src={photo}
              alt={meta.name}
              loading="lazy"
              className="w-full h-[220px] object-cover"
              data-testid="join-photo"
            />
          ) : (
            <div className="w-full h-[180px] grid place-items-center bg-[#0a0a0a]" data-testid="join-photo-empty">
              <Crown className="w-10 h-10 text-gold" strokeWidth={1.25} />
            </div>
          )}
          {meta.vip_enabled && (
            <span className="pill pill-vip absolute top-3 left-3">
              <Crown className="w-3 h-3" strokeWidth={2}/> VIP
            </span>
          )}
          {meta.verified && (
            <span className="pill absolute top-3 left-3 bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">
              <ShieldCheck className="w-3 h-3" strokeWidth={2}/> Verified
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="font-display text-3xl text-foreground leading-tight" data-testid="join-name">{meta.name}</div>
          {subtitle && (
            <div className="mt-1.5 text-[12px] text-foreground/55 tracking-wide flex items-center gap-1.5" data-testid="join-subtitle">
              {meta.type === "business" && <MapPin className="w-3 h-3" strokeWidth={1.5}/>}
              {subtitle}
            </div>
          )}
          {meta.ratings_average != null && meta.ratings_count > 0 && (
            <div className="mt-3 flex items-center gap-2 text-[12px] text-foreground/75 font-mono">
              <Star className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} fill="currentColor"/>
              {meta.ratings_average.toFixed(2)}
              <span className="text-foreground/45">({meta.ratings_count})</span>
            </div>
          )}
          {meta.description && (
            <p className="mt-3 text-sm text-foreground/65 leading-relaxed line-clamp-4">{meta.description}</p>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Customer actions                                                      */
/* ------------------------------------------------------------------ */
function GuestActions({ meta }) {
  const { t } = useI18n();
  return (
    <>
      <button
        data-install-trigger
        data-testid="join-open-app"
        className="btn-gold w-full justify-center"
        style={{ minHeight: 56 }}
      >
        <Smartphone className="w-4 h-4" strokeWidth={1.5}/> {t("join_open_app")}
      </button>
      <Link
        to={meta.deep_link}
        data-testid="join-continue-guest"
        className="btn-outline w-full justify-center"
        style={{ minHeight: 52 }}
      >
        <Sparkles className="w-4 h-4" strokeWidth={1.5}/> {t("auth_continue_guest")}
      </Link>
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/select-role"
          data-testid="join-signup"
          className="btn-outline justify-center"
          style={{ minHeight: 52 }}
        >
          <UserPlus className="w-4 h-4" strokeWidth={1.5}/> {t("auth_create_account")}
        </Link>
        <Link
          to="/login"
          data-testid="join-signin"
          className="btn-outline justify-center"
          style={{ minHeight: 52 }}
        >
          <LogIn className="w-4 h-4" strokeWidth={1.5}/> {t("auth_sign_in")}
        </Link>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Logged-in actions — entity-specific                                */
/* ------------------------------------------------------------------ */
function LoggedInActions({ meta, busy, onCheckIn, onSave, onAddFriend }) {
  if (meta.type === "business") {
    return (
      <>
        <button onClick={onCheckIn} disabled={busy} data-testid="join-checkin" className="btn-gold w-full justify-center" style={{ minHeight: 56 }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Check className="w-4 h-4" strokeWidth={1.5}/>}
          Check In Here
        </button>
        <button onClick={onSave} disabled={busy} data-testid="join-save-place" className="btn-outline w-full justify-center" style={{ minHeight: 52 }}>
          <Heart className="w-4 h-4" strokeWidth={1.5}/> Save this Place
        </button>
        <Link to={meta.deep_link} data-testid="join-view-profile" className="btn-outline w-full justify-center" style={{ minHeight: 52 }}>
          <Sparkles className="w-4 h-4" strokeWidth={1.5}/> View full profile
        </Link>
      </>
    );
  }
  if (meta.type === "vendor") {
    return (
      <>
        <button onClick={onSave} disabled={busy} data-testid="join-save-vendor" className="btn-gold w-full justify-center" style={{ minHeight: 56 }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Heart className="w-4 h-4" strokeWidth={1.5}/>}
          Save Vendor
        </button>
        <Link to={meta.deep_link} data-testid="join-view-vendor" className="btn-outline w-full justify-center" style={{ minHeight: 52 }}>
          <Sparkles className="w-4 h-4" strokeWidth={1.5}/> View full profile
        </Link>
      </>
    );
  }
  if (meta.type === "user") {
    return (
      <>
        <button onClick={onAddFriend} disabled={busy} data-testid="join-add-friend" className="btn-gold w-full justify-center" style={{ minHeight: 56 }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <UserPlus className="w-4 h-4" strokeWidth={1.5}/>}
          Add Friend
        </button>
        <Link to="/friends" data-testid="join-open-friends" className="btn-outline w-full justify-center" style={{ minHeight: 52 }}>
          <Sparkles className="w-4 h-4" strokeWidth={1.5}/> Go to Friends
        </Link>
      </>
    );
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Desktop fallback — QR code to scan with phone                      */
/* ------------------------------------------------------------------ */
function DesktopShareView({ meta }) {
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&bgcolor=0B0B0B&color=C8A063&format=svg&margin=0&data=${encodeURIComponent(meta.share_url)}`;
  return (
    <main className="min-h-screen grid place-items-center bg-background px-6" data-testid="join-desktop">
      <div className="lux-card p-10 max-w-md text-center">
        <Monogram size={36} />
        <div className="eyebrow mt-4">For the best experience</div>
        <h1 className="font-display text-3xl mt-2 text-foreground">Open this on your phone</h1>
        <p className="text-sm text-foreground/55 mt-3">
          Habano.app is a mobile-first experience. Scan the code below with your phone camera to view {meta.name} on the go.
        </p>
        <div className="mt-6 rounded-2xl p-4 inline-block" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(232,184,75,0.18)" }}>
          <img src={qr} alt="Scan with phone" width={240} height={240} />
        </div>
        <div className="mt-4 text-[11px] text-foreground/45 font-mono break-all">{meta.share_url}</div>
      </div>
    </main>
  );
}
